const getNowMs = () => {
  if (typeof performance !== "undefined" && typeof performance.now === "function")
    return performance.now();
  return Date.now();
};

const roundMs = (ms) => Math.round(ms);

const isPublicIpDisabled = () =>
  process.env.REACT_APP_DEVICEINFO_DISABLE_PUBLIC_IP === "true";

const isLocalIpDisabled = () =>
  process.env.REACT_APP_DEVICEINFO_DISABLE_LOCAL_IP === "true";

const isGeoDisabled = () =>
  process.env.REACT_APP_DEVICEINFO_DISABLE_GEOLOCATION === "true";

// Get screen dimensions (best-effort; returns empty strings in non-browser tests)
const getScreenDimensions = () => {
  const w = typeof window !== "undefined" ? window : null;
  const screen = w && w.screen ? w.screen : null;
  const width = screen && typeof screen.width === "number" ? screen.width : null;
  const height =
    screen && typeof screen.height === "number" ? screen.height : null;

  return {
    width: width === null ? "" : width.toString(),
    height: height === null ? "" : height.toString(),
  };
};

// Get timezone (best-effort; returns empty string in non-browser tests)
const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (_err) {
    return "";
  }
};

const getBaseHeaders = () => {
  const screen = getScreenDimensions();
  const timezone = getTimezone();
  return {
    "x-screen-width": screen.width,
    "x-screen-height": screen.height,
    "x-timezone": timezone,
  };
};

const getEmptyHeaders = () => ({
  "x-screen-width": "",
  "x-screen-height": "",
  "x-timezone": "",
  "x-latitude": "",
  "x-longitude": "",
  "x-country": "",
  "x-region": "",
  "x-city": "",
  "x-public-ipv4": "",
  "x-public-ipv6": "",
  "x-local-ipv4": "",
  "x-local-ipv6": "",
});

const getEmptyMetrics = () => ({
  public_ip_fetch_ms: null,
  local_ip_webrtc_ms: null,
  ip_geolocation_ms: null,
  device_info_total_ms: null,
  collector_disabled_public_ip: isPublicIpDisabled(),
  collector_disabled_local_ip: isLocalIpDisabled(),
  collector_disabled_geo: isGeoDisabled() || isPublicIpDisabled(),
  collector_timeout_public_ip: false,
  collector_timeout_local_ip: false,
  collector_timeout_geo: false,
  had_public_ipv4: false,
  had_public_ipv6: false,
  had_local_ipv4: false,
  had_local_ipv6: false,
  had_geo_fields: false,
});

let cachedHeaders = { ...getEmptyHeaders() };
let cachedMetrics = { ...getEmptyMetrics() };
let deviceInfoPromise = null;

const fetchIpJson = async (url) => {
  const startedAt = getNowMs();
  try {
    const response = await fetch(url);
    const data = await response.json();
    const ip = data && typeof data.ip === "string" ? data.ip : null;
    return { ip, ms: roundMs(getNowMs() - startedAt) };
  } catch (_err) {
    return { ip: null, ms: roundMs(getNowMs() - startedAt) };
  }
};

const collectPublicIps = async () => {
  const startedAt = getNowMs();

  const [ipify, api64] = await Promise.all([
    fetchIpJson("https://api.ipify.org?format=json"),
    fetchIpJson("https://api64.ipify.org?format=json"),
  ]);

  const publicIps = { ipv4: "", ipv6: "" };
  const candidateIps = [ipify.ip, api64.ip].filter(Boolean);
  for (const candidate of candidateIps) {
    if (typeof candidate !== "string") continue;
    if (candidate.includes(":")) publicIps.ipv6 = candidate;
    else publicIps.ipv4 = candidate;
  }

  const totalMs = roundMs(getNowMs() - startedAt);
  return { ...publicIps, totalMs };
};

const collectLocalIps = async () => {
  const startedAt = getNowMs();

  const localIps = { ipv4: "", ipv6: "" };
  let timedOut = false;

  try {
    if (typeof RTCPeerConnection === "undefined") {
      return {
        ...localIps,
        totalMs: roundMs(getNowMs() - startedAt),
        timedOut: false,
      };
    }

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
      ],
    });

    pc.createDataChannel("");

    const ipPromise = new Promise((resolve) => {
      const timeout = setTimeout(() => {
        timedOut = true;
        pc.close();
        resolve();
      }, 10000);

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          const candidate = event.candidate.candidate;

          const ipv4Regex =
            /([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3})/;
          const ipv6Regex = /([0-9a-fA-F]{1,4}(?::[0-9a-fA-F]{1,4}){7})/;

          const ipv4Match = candidate.match(ipv4Regex);
          const ipv6Match = candidate.match(ipv6Regex);

          if (ipv4Match && !localIps.ipv4) {
            const ip = ipv4Match[1];
            if (ip && ip !== "0.0.0.0" && ip !== "127.0.0.1") {
              localIps.ipv4 = ip;
            }
          }
          if (ipv6Match && !localIps.ipv6) {
            const ip = ipv6Match[1];
            if (ip && ip !== "::1") {
              localIps.ipv6 = ip;
            }
          }

          if (localIps.ipv4 && localIps.ipv6) {
            clearTimeout(timeout);
            pc.close();
            resolve();
          }
        } else {
          clearTimeout(timeout);
          pc.close();
          resolve();
        }
      };
    });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await ipPromise;
  } catch (_err) {
    // Best-effort: keep empty values and move on.
  }

  return {
    ...localIps,
    totalMs: roundMs(getNowMs() - startedAt),
    timedOut,
  };
};

const collectGeolocation = async (ipToUse) => {
  const startedAt = getNowMs();
  try {
    const response = await fetch(`https://ipapi.co/${ipToUse}/json/`);
    const data = await response.json();
    if (data && !data.error) {
      return {
        latitude: data.latitude?.toString?.() || "",
        longitude: data.longitude?.toString?.() || "",
        country: data.country_name || "",
        region: data.region || "",
        city: data.city || "",
        totalMs: roundMs(getNowMs() - startedAt),
      };
    }
  } catch (_err) {
    // Best-effort: keep empty values and move on.
  }
  return {
    latitude: "",
    longitude: "",
    country: "",
    region: "",
    city: "",
    totalMs: roundMs(getNowMs() - startedAt),
  };
};

export const getDeviceInfoHeadersSnapshot = () => {
  const base = getBaseHeaders();
  const disablePublic = isPublicIpDisabled();
  const disableLocal = isLocalIpDisabled();
  const disableGeo = isGeoDisabled() || disablePublic;

  return {
    ...getEmptyHeaders(),
    ...base,
    "x-public-ipv4": disablePublic ? "" : cachedHeaders["x-public-ipv4"] || "",
    "x-public-ipv6": disablePublic ? "" : cachedHeaders["x-public-ipv6"] || "",
    "x-local-ipv4": disableLocal ? "" : cachedHeaders["x-local-ipv4"] || "",
    "x-local-ipv6": disableLocal ? "" : cachedHeaders["x-local-ipv6"] || "",
    "x-latitude": disableGeo ? "" : cachedHeaders["x-latitude"] || "",
    "x-longitude": disableGeo ? "" : cachedHeaders["x-longitude"] || "",
    "x-country": disableGeo ? "" : cachedHeaders["x-country"] || "",
    "x-region": disableGeo ? "" : cachedHeaders["x-region"] || "",
    "x-city": disableGeo ? "" : cachedHeaders["x-city"] || "",
  };
};

export const getDeviceInfoMetricsSnapshot = () => ({ ...cachedMetrics });

export const startDeviceInfoCollection = () => {
  if (deviceInfoPromise) return deviceInfoPromise;

  const startedAt = getNowMs();
  cachedMetrics = { ...getEmptyMetrics() };
  cachedHeaders = { ...cachedHeaders, ...getBaseHeaders() };

  const disablePublic = isPublicIpDisabled();
  const disableLocal = isLocalIpDisabled();
  const disableGeo = isGeoDisabled() || disablePublic;

  const publicPromise = disablePublic
    ? Promise.resolve({ ipv4: "", ipv6: "", totalMs: null })
    : collectPublicIps();

  const localPromise = disableLocal
    ? Promise.resolve({ ipv4: "", ipv6: "", totalMs: null, timedOut: false })
    : collectLocalIps();

  deviceInfoPromise = (async () => {
    const publicIps = await publicPromise;

    if (!disablePublic) {
      cachedMetrics.public_ip_fetch_ms = publicIps.totalMs;
      cachedMetrics.had_public_ipv4 = Boolean(publicIps.ipv4);
      cachedMetrics.had_public_ipv6 = Boolean(publicIps.ipv6);
      cachedHeaders["x-public-ipv4"] = publicIps.ipv4 || "";
      cachedHeaders["x-public-ipv6"] = publicIps.ipv6 || "";
    }

    const ipToUse = !disableGeo ? publicIps.ipv4 || publicIps.ipv6 || "" : "";
    const geoPromise =
      !disableGeo && ipToUse ? collectGeolocation(ipToUse) : Promise.resolve(null);

    await Promise.all([
      localPromise.then((result) => {
        if (!disableLocal) {
          cachedMetrics.local_ip_webrtc_ms = result.totalMs;
          cachedMetrics.collector_timeout_local_ip = Boolean(result.timedOut);
          cachedMetrics.had_local_ipv4 = Boolean(result.ipv4);
          cachedMetrics.had_local_ipv6 = Boolean(result.ipv6);
          cachedHeaders["x-local-ipv4"] = result.ipv4 || "";
          cachedHeaders["x-local-ipv6"] = result.ipv6 || "";
        }
        return result;
      }),
      geoPromise.then((result) => {
        if (!disableGeo) {
          if (result) {
            cachedMetrics.ip_geolocation_ms = result.totalMs;
            cachedMetrics.had_geo_fields = Boolean(
              result.country ||
                result.region ||
                result.city ||
                result.latitude ||
                result.longitude
            );
            cachedHeaders["x-latitude"] = result.latitude || "";
            cachedHeaders["x-longitude"] = result.longitude || "";
            cachedHeaders["x-country"] = result.country || "";
            cachedHeaders["x-region"] = result.region || "";
            cachedHeaders["x-city"] = result.city || "";
          } else {
            cachedMetrics.ip_geolocation_ms = null;
            cachedMetrics.had_geo_fields = false;
          }
        }
        return result;
      }),
    ]);

    cachedMetrics.device_info_total_ms = roundMs(getNowMs() - startedAt);

    return {
      headers: getDeviceInfoHeadersSnapshot(),
      metrics: getDeviceInfoMetricsSnapshot(),
    };
  })();

  return deviceInfoPromise;
};

// Backwards-compatible API: returns a full set of headers, but should not be
// awaited on the verification critical path.
export const getDeviceInfo = async () => {
  await startDeviceInfoCollection();
  return getDeviceInfoHeadersSnapshot();
};
